import React, { useState, useEffect } from "react";
import { StyleSheet, TextInput, View, ScrollView } from "react-native";
import { z } from "zod";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomAdminModal from "../../../components/common/general/CustomAdminModal/CustomAdminModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import ImageUpload from "../../../components/common/general/ImageUpload/ImageUpload";
import { Course, StoragePathType } from "../../../graphql/generated/graphql";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import AddCourseSecondStep from "./AddCourseSecondStep";
import CurrencyInput from "react-native-currency-input";
import { useAppConfig } from "../../../AppConfigProvider";
import AddCourseDegreesStep from "./AddCourseDegreesStep";

const modals = {
  editConfirmation: {
    title: "Success!",
    ariaLabel: "Course Added Successfully Modal",
    warningText: "The course has been Edited.",
    actionButtonText: "OK",
  },
  editError: {
    title: "Error!",
    ariaLabel: "Course Edition Error Modal",
    warningText: "You may not have permission to edit the course.",
    actionButtonText: "CLOSE",
  },
  noChanges: {
    title: "No Changes Made",
    ariaLabel: "No Changes Modal",
    warningText: "You didn't make any changes to the course.",
    actionButtonText: "OK",
  },
};

export enum EditCourseModalState {
  CLOSED = "CLOSED",
  EDIT_CONFIRMATION = "EDIT_CONFIRMATION",
  EDIT_ERROR = "EDIT_ERROR",
  NO_CHANGES = "NO_CHANGES",
}

const courseSchema = z.object({
  courseName: z.string().min(1, "Course name is required"),
  courseCode: z.string().optional(),
  degrees: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  thumbnail: z.string().optional(),
  description: z.string().optional(),
  objectives: z.string().optional(),
  targetAudience: z.string().optional(),
  cost: z.number().optional(),
});

type CourseFormData = z.infer<typeof courseSchema>;

type SubmitCourseFormData = Omit<CourseFormData, "degrees"> & {
  degrees?: string[];
};

interface EditCourseModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (formData: SubmitCourseFormData) => Promise<{
    success: boolean;
  }>;
  courseData?: Course;
}

const EditCourseModal: React.FC<EditCourseModalProps> = ({ visible, onClose, onSubmit, courseData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isMedium } = useResponsiveScreen();
  const [modalState, setModalState] = useState({
    state: EditCourseModalState.CLOSED,
  });
  const { hasDegrees } = useAppConfig();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [formStep, setFormStep] = useState<"first" | "hasDegrees" | "second">("first");

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
    trigger,
  } = useForm<CourseFormData>({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      courseName: "",
      courseCode: "",
      degrees: [],
      thumbnail: "",
      description: "",
      objectives: "",
      targetAudience: "",
      cost: 0,
    },
  });

  useEffect(() => {
    if (courseData) {
      reset({
        courseName: courseData.name || "",
        courseCode: courseData.course_code || "",
        degrees:
          courseData.degreesHasCourse?.map((d) => ({
            id: d.id,
            name: d.name,
          })) || [],
        thumbnail: courseData.thumbnail || "",
        description: courseData.description || "",
        objectives: Array.isArray(courseData.objectives)
          ? courseData.objectives.join("\n")
          : courseData.objectives || "",
        targetAudience: courseData.targetAudience || "",
        cost: courseData.cost ?? 0,
      });
    }
  }, [courseData, reset]);

  const handleClose = () => {
    onClose();
    reset();
    setFormStep("first");
  };

  const hasChanges = (formData: CourseFormData): boolean => {
    return (
      formData.courseName !== courseData.name ||
      formData.courseCode !== courseData.course_code ||
      (formData.description || "") !== (courseData.description || "") ||
      (formData.objectives || "") !==
        (Array.isArray(courseData.objectives) ? courseData.objectives.join("\n") : courseData.objectives || "") ||
      (formData.targetAudience || "") !== (courseData.targetAudience || "") ||
      formData.cost !== (courseData.cost ?? 0) ||
      JSON.stringify(formData.degrees?.map((d) => d.id) ?? []) !==
        JSON.stringify(courseData.degreesHasCourse?.map((d) => d.id) ?? [])
    );
  };

  const handleFormSubmit = React.useCallback(
    async (formData: CourseFormData) => {
      if (!hasChanges(formData)) {
        setModalState({ state: EditCourseModalState.NO_CHANGES });
        return;
      }

      try {
        setIsSubmitting(true);

        const result = await onSubmit({
          courseName: formData.courseName,
          courseCode: formData.courseCode,
          description: formData.description,
          objectives: formData.objectives,
          cost: formData.cost,
          degrees: formData.degrees?.map((d) => d.id),
          targetAudience: formData.targetAudience,
          thumbnail: formData.thumbnail,
        });

        if (!result || !result.success) {
          setModalState({ state: EditCourseModalState.EDIT_ERROR });
          setIsSubmitting(false);
          return;
        }

        handleClose();
        setModalState({ state: EditCourseModalState.EDIT_CONFIRMATION });
        setIsSubmitting(false);
      } catch (error) {
        setModalState({ state: EditCourseModalState.EDIT_ERROR });
        setIsSubmitting(false);
      }
    },
    [courseData, handleClose]
  );

  const isFirstStep = formStep === "first";
  const isSecondStep = formStep === "second";
  const isDegreesStep = formStep === "hasDegrees";

  const handleNext = async () => {
    if (formStep === "first") {
      const isValid = await trigger(["courseName", "courseCode"]);
      if (isValid) {
        if (hasDegrees) {
          setFormStep("hasDegrees");
        } else {
          setFormStep("second");
        }
      }
    } else if (formStep === "hasDegrees") {
      setFormStep("second");
    }
  };

  const handleBackOrCancel = () => {
    if (formStep === "second") {
      if (hasDegrees) {
        setFormStep("hasDegrees");
      } else {
        setFormStep("first");
      }
    } else if (formStep === "hasDegrees") {
      setFormStep("first");
    } else {
      handleClose();
    }
  };

  return (
    <>
      <CustomAdminModal
        visible={visible}
        onRequestClose={handleClose}
        title="Edit a Course"
        ariaLabel="Edit course modal"
        style={{ width: isMedium ? "50%" : "90%", maxHeight: "90vh" }}
        onShow={() => {}}>
        <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true}>
          <View style={styles.container}>
            {isFirstStep && (
              <>
                <View style={styles.formGroup}>
                  <CustomText
                    text="Course Name"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <CustomSpacing size="s" type="vertical" />
                  <Controller
                    control={control}
                    name="courseName"
                    render={({ field: { onChange, value } }) => (
                      <CustomTextInput
                        value={value}
                        onChangeText={onChange}
                        placeholder="Understanding Design Change Types"
                        style={styles.input}
                      />
                    )}
                  />
                  {errors?.courseName?.message && (
                    <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.courseName?.message} />
                  )}
                </View>

                <View style={styles.formGroup}>
                  <CustomText
                    text="Course Code (Optional)"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <CustomText
                    text="Enter a course identifier (e.g., ENG101, MATH201)"
                    size="xs"
                    style={styles.helperText}
                    useTranslationText={false}
                  />
                  <Controller
                    control={control}
                    name="courseCode"
                    render={({ field: { onChange, value } }) => (
                      <CustomTextInput
                        value={value}
                        onChangeText={onChange}
                        placeholder="ENG101"
                        style={styles.input}
                      />
                    )}
                  />
                  {errors?.courseCode?.message && (
                    <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.courseCode?.message} />
                  )}
                </View>

                <View style={styles.formGroup}>
                  <CustomText
                    text="Upload a course thumbnail"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <CustomText
                    text="We'll generate a thumbnail for you if you leave this blank. Recommended size: 800×450"
                    size="xs"
                    style={styles.helperText}
                  />
                  {uploadError && <CustomText text={uploadError} size="xs" style={styles.errorText} />}
                  <View style={styles.uploadContainer}>
                    <Controller
                      control={control}
                      name="thumbnail"
                      render={({ field: { onChange, value } }) => (
                        <ImageUpload
                          onChange={onChange}
                          value={value}
                          storagePath={StoragePathType.Thumbnails}
                          aspectRatio={[16, 9]}
                          allowsEditing={true}
                          onError={(error) => setUploadError(error)}
                        />
                      )}
                    />
                  </View>
                </View>

                <View style={styles.formGroup}>
                  <CustomText
                    text="Course Cost"
                    size="s"
                    textType="text"
                    style={{ color: "#182230" }}
                    weight="ultraBold"
                  />
                  <Controller
                    control={control}
                    name="cost"
                    render={({ field: { onChange, value } }) => (
                      <CurrencyInput
                        renderTextInput={(textInputProps) => (
                          <TextInput style={styles.currencyInput} {...textInputProps} />
                        )}
                        value={value}
                        onChangeValue={onChange}
                        delimiter=","
                        separator="."
                        precision={2}
                        minValue={0}
                        keyboardType="numeric"
                        placeholder="Enter amount"
                        prefix="$"
                      />
                    )}
                  />
                  {errors?.cost?.message && (
                    <CustomText size="xs" style={{ color: "#EF4444" }} text={errors.cost?.message} />
                  )}
                </View>
              </>
            )}

            {isDegreesStep && <AddCourseDegreesStep control={control} />}
            {isSecondStep && <AddCourseSecondStep control={control} />}

            <View style={styles.buttonContainer}>
              <CustomButton
                text={isFirstStep ? "Cancel" : "Back"}
                onPress={handleBackOrCancel}
                styleType="secondary"
                size="m"
                style={styles.cancelButton}
                textStyle={styles.cancelButtonText}
                leftIcon={null}
                rightIcon={null}
                disabled={false}
                textProps={{}}
              />
              {(isFirstStep || isDegreesStep) && (
                <CustomButton
                  text={"Next"}
                  onPress={handleNext}
                  styleType="primary"
                  size="m"
                  style={styles.addButton}
                  textStyle={styles.addButtonText}
                  leftIcon={null}
                  rightIcon={null}
                  disabled={false}
                  textProps={{}}
                />
              )}
              {isSecondStep && (
                <CustomButton
                  text={"Submit"}
                  onPress={handleSubmit(handleFormSubmit)}
                  styleType="primary"
                  size="m"
                  style={!isValid || isSubmitting ? { backgroundColor: "#ccc" } : styles.addButton}
                  textStyle={styles.addButtonText}
                  leftIcon={null}
                  rightIcon={null}
                  disabled={!isValid || isSubmitting}
                  textProps={{}}
                />
              )}
            </View>
          </View>
        </ScrollView>
      </CustomAdminModal>
      <NewCustomAxioActionModal
        visible={modalState.state === EditCourseModalState.EDIT_CONFIRMATION}
        onClose={() => setModalState({ state: EditCourseModalState.CLOSED })}
        modalTexts={modals.editConfirmation}
        contextualText={getValues("courseName")}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === EditCourseModalState.EDIT_ERROR}
        onClose={() => setModalState({ state: EditCourseModalState.CLOSED })}
        modalTexts={modals.editError}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === EditCourseModalState.NO_CHANGES}
        onClose={() => setModalState({ state: EditCourseModalState.CLOSED })}
        modalTexts={modals.noChanges}
      />
    </>
  );
};

const styles = StyleSheet.create({
  modalScrollView: {
    flex: 1,
    width: "100%",
  },
  container: {
    padding: 24,
  },
  formGroup: {
    marginBottom: 24,
  },
  helperText: {
    color: "#6B7280",
    marginBottom: 8,
  },
  input: {
    width: "100%",
  },
  currencyInput: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    color: "#1F2937",
    fontSize: 14,
    borderRadius: 8,
    width: "100%",
    padding: 10,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  addButton: {
    // backgroundColor: "#3E68FE",
    borderRadius: 6,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: "#4B5563",
  },
  addButtonText: {
    color: "#FFFFFF",
  },
  errorText: {
    color: "#EF4444",
    marginTop: 4,
  },
  uploadContainer: {
    marginTop: 2,
  },
});

export default EditCourseModal;
